import { DieTypes } from "@/constants";
import { customSetIdPrefix } from "@/constants/diceConfig";
import type { CustomUrlMap, DieType } from "@/types";
import { create } from "zustand";
import { persist } from "zustand/middleware";

type SelectedSet = {
  fullName: string;
  id?: string;
};
type DicePreviewerState = {
  selectedSet?: SelectedSet;
  setSelectedSet: (selectedSet: SelectedSet | undefined) => void;
  additionalSetIds: string[];
  setAdditionalSetIds: (ids: string[]) => void;
  toggleAdditionalSet: (id: string) => void;
  dieType: DieType;
  setDieType: (dieType: DieType) => void;
  customUrlMap?: CustomUrlMap;
  setCustomUrlMap: (customUrlMap: CustomUrlMap | undefined) => void;
  customSetOverrideId: string;
  setCustomSetOverrideId: (customSetOverrideId: string) => void;
};

export const useDicePreviewerStore = create<DicePreviewerState>()(
  persist(
    (set, get) => ({
      setSelectedSet: (selectedSet) => set(() => ({ selectedSet })),
      additionalSetIds: [],
      setAdditionalSetIds: (additionalSetIds) =>
        set(() => ({ additionalSetIds })),
      toggleAdditionalSet: (id) => {
        const { additionalSetIds, selectedSet } = get();

        if (id === selectedSet?.id) return;

        set(() => ({
          additionalSetIds: additionalSetIds.includes(id)
            ? additionalSetIds.filter((existing) => existing !== id)
            : [...additionalSetIds, id],
        }));
      },
      dieType: DieTypes.d20,
      setDieType: (dieType) => set(() => ({ dieType })),
      setCustomUrlMap: (customUrlMap) => set(() => ({ customUrlMap })),
      customSetOverrideId: "",
      setCustomSetOverrideId: (customSetOverrideId) =>
        set(() => ({ customSetOverrideId })),
    }),
    {
      name: "dicePreviewerState",
      partialize: (state) => ({
        selectedSet:
          // Avoid persisting custom sets, since files are temporary
          state.selectedSet?.id?.startsWith(customSetIdPrefix)
            ? { fullName: "" }
            : state.selectedSet,
        additionalSetIds: state.additionalSetIds.filter(
          (id) => !id.startsWith(customSetIdPrefix),
        ),
        dieType: state.dieType,
      }),
    },
  ),
);
